<template>
  <!-- Records a voice message in place of the composer's text box. It starts
       recording as soon as it opens: the red dot, the time and the live
       level bars show it is listening. Stop to listen back first, or send
       straight away; the bin throws it away. -->
  <div class="sc-recorder" :class="`is-${state}`" role="group" aria-label="Voice message">
    <button
      type="button"
      class="sc-recorder__btn sc-recorder__btn--discard"
      title="Discard"
      aria-label="Discard voice message"
      @click="discard"
    >
      <i class="pi pi-trash" aria-hidden="true"></i>
    </button>

    <div v-if="state === 'review' && previewUrl" class="sc-recorder__review">
      <chat-voice-player :src="previewUrl" :duration="seconds" :waveform="waveform" mine />
    </div>
    <div v-else class="sc-recorder__live" aria-live="polite">
      <span class="sc-recorder__dot" aria-hidden="true"></span>
      <span class="sc-recorder__time">{{ state === 'starting' ? 'Starting…' : formatChatDuration(seconds) }}</span>
      <span class="sc-recorder__levels" aria-hidden="true">
        <span v-for="(level, index) in liveLevels" :key="index" :style="{ height: `${Math.round(12 + level * 88)}%` }"></span>
      </span>
      <span v-if="nearLimit" class="sc-recorder__limit">{{ formatChatDuration(maxSeconds - seconds) }} left</span>
    </div>

    <button
      v-if="state === 'recording'"
      type="button"
      class="sc-recorder__btn sc-recorder__btn--stop"
      title="Stop and listen"
      aria-label="Stop recording and listen"
      @click="stop"
    >
      <i class="pi pi-stop-circle" aria-hidden="true"></i>
    </button>
    <button
      type="button"
      class="sc-recorder__btn sc-recorder__btn--send"
      :disabled="state === 'starting' || state === 'finishing'"
      title="Send"
      aria-label="Send voice message"
      @click="send"
    >
      <i class="pi pi-send" aria-hidden="true"></i>
    </button>
  </div>
</template>
<script>
import chatVoicePlayer from '@/components/chat/Chat_Voice_Player.vue';
import { canRecordVoice, CHAT_VOICE_MAX_SECONDS, formatChatDuration } from '@/utils/chat';
import { TOAST_GLOBAL_CONFIG } from '@/utils/const';

// What the browser can record in, best first: Opus in WebM (Chrome, Edge,
// Firefox), then MP4 / AAC (Safari), then Ogg.
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/aac', 'audio/ogg;codecs=opus', 'audio/ogg'];
const EXTENSIONS = { 'audio/webm': '.webm', 'audio/mp4': '.m4a', 'audio/aac': '.aac', 'audio/ogg': '.ogg', 'audio/mpeg': '.mp3', 'audio/wav': '.wav' };

const LIVE_BARS = 28;
const WAVEFORM_BARS = 48;
const SAMPLE_EVERY_MS = 80;
// Shorter than this is a slip of the finger, not a message.
const MIN_SECONDS = 0.6;

function pickMimeType() {
  if (typeof MediaRecorder.isTypeSupported !== 'function') return '';
  return MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) || '';
}

function extensionFor(type) {
  return EXTENSIONS[String(type || '').split(';')[0].trim().toLowerCase()] || '.webm';
}

// The levels taken while recording, as `count` bars from 0 to 1.
function waveformOf(levels, count) {
  if (!levels.length) return [];
  const bars = Array.from({ length: count }, (_, index) => {
    const from = Math.floor((index / count) * levels.length);
    const to = Math.max(from + 1, Math.floor(((index + 1) / count) * levels.length));
    const slice = levels.slice(from, to);
    return slice.length ? Math.max(...slice) : 0;
  });
  const peak = Math.max(...bars, 0.05);
  return bars.map((bar) => Math.round(Math.min(bar / peak, 1) * 100) / 100);
}

export default {
  name: 'ChatVoiceRecorder',
  components: {
    chatVoicePlayer,
  },
  emits: ['send', 'cancel'],
  data() {
    return {
      // starting → recording → (review) → sent, or finishing while it stops to send.
      state: 'starting',
      seconds: 0,
      liveLevels: Array.from({ length: LIVE_BARS }, () => 0),
      previewUrl: '',
      waveform: [],
      maxSeconds: CHAT_VOICE_MAX_SECONDS,
    };
  },
  computed: {
    nearLimit() {
      return this.state === 'recording' && this.maxSeconds - this.seconds <= 30;
    },
  },
  methods: {
    formatChatDuration,
    fail(message) {
      this.$toast.error(message, TOAST_GLOBAL_CONFIG);
      this.release();
      this.$emit('cancel');
    },
    async start() {
      if (!canRecordVoice()) {
        this.fail("Voice messages can't be recorded in this browser.");
        return;
      }
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      } catch (error) {
        const name = error && error.name;
        this.fail(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? 'Microphone access is blocked. Allow it for this site in your browser, then try again.'
            : name === 'NotFoundError' || name === 'OverconstrainedError'
              ? 'No microphone was found.'
              : "The microphone couldn't be started. Check that no other app is using it."
        );
        return;
      }
      if (this.isGone) {
        this.release();
        return;
      }

      this.mimeType = pickMimeType();
      try {
        this.recorder = new MediaRecorder(this.stream, this.mimeType ? { mimeType: this.mimeType, audioBitsPerSecond: 48000 } : undefined);
      } catch (error) {
        this.fail("Voice messages can't be recorded in this browser.");
        return;
      }
      this.chunks = [];
      this.recorder.ondataavailable = (event) => {
        if (event.data && event.data.size) this.chunks.push(event.data);
      };
      this.recorder.onstop = () => this.finish();
      this.recorder.start(250);

      this.listen();
      this.startedAt = performance.now();
      this.state = 'recording';
      this.tick();
    },
    // The level meter: loudness off the microphone, sampled while recording.
    listen() {
      try {
        const Context = window.AudioContext || window.webkitAudioContext;
        if (!Context) return;
        this.audioContext = new Context();
        const source = this.audioContext.createMediaStreamSource(this.stream);
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 1024;
        source.connect(this.analyser);
        this.samples = new Uint8Array(this.analyser.fftSize);
      } catch (error) {
        this.analyser = null;
      }
    },
    level() {
      if (!this.analyser) return 0.15 + Math.random() * 0.2;
      this.analyser.getByteTimeDomainData(this.samples);
      let sum = 0;
      for (let index = 0; index < this.samples.length; index += 1) {
        const value = (this.samples[index] - 128) / 128;
        sum += value * value;
      }
      return Math.min(1, Math.sqrt(sum / this.samples.length) * 4);
    },
    tick() {
      if (this.state !== 'recording') return;
      const now = performance.now();
      this.seconds = (now - this.startedAt) / 1000;
      if (!this.lastSampleAt || now - this.lastSampleAt >= SAMPLE_EVERY_MS) {
        this.lastSampleAt = now;
        const level = this.level();
        this.levels.push(level);
        this.liveLevels = [...this.liveLevels.slice(1), level];
      }
      if (this.seconds >= this.maxSeconds) {
        this.stop();
        return;
      }
      this.frame = window.requestAnimationFrame(this.tick);
    },
    stop() {
      if (this.state !== 'recording') return;
      this.seconds = Math.min((performance.now() - this.startedAt) / 1000, this.maxSeconds);
      this.state = 'finishing';
      this.recorder.stop();
    },
    send() {
      if (this.state === 'review') {
        this.deliver();
        return;
      }
      if (this.state === 'recording') {
        this.sendWhenDone = true;
        this.stop();
      }
    },
    finish() {
      if (this.discarded || this.isGone) return;
      const type = (this.recorder && this.recorder.mimeType) || this.mimeType || 'audio/webm';
      this.blob = new Blob(this.chunks, { type });
      this.waveform = waveformOf(this.levels, WAVEFORM_BARS);
      this.releaseInput();

      if (!this.blob.size || this.seconds < MIN_SECONDS) {
        this.$toast.info('Hold the recording a little longer — that was too short to send.', TOAST_GLOBAL_CONFIG);
        this.$emit('cancel');
        return;
      }
      if (this.sendWhenDone) {
        this.deliver();
        return;
      }
      this.previewUrl = URL.createObjectURL(this.blob);
      this.state = 'review';
    },
    deliver() {
      if (!this.blob) return;
      const type = this.blob.type || 'audio/webm';
      this.$emit('send', {
        blob: this.blob,
        duration: Math.round(this.seconds * 10) / 10,
        waveform: this.waveform,
        name: `voice-message-${Date.now()}${extensionFor(type)}`,
      });
    },
    discard() {
      this.discarded = true;
      if (this.recorder && this.recorder.state !== 'inactive') {
        try {
          this.recorder.stop();
        } catch (error) {
          // Already stopped.
        }
      }
      this.release();
      this.$emit('cancel');
    },
    // Lets go of the microphone (the browser's recording light goes out).
    releaseInput() {
      window.cancelAnimationFrame(this.frame);
      if (this.stream) this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
      if (this.audioContext && this.audioContext.state !== 'closed') this.audioContext.close().catch(() => {});
      this.audioContext = null;
      this.analyser = null;
    },
    release() {
      this.releaseInput();
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = '';
    },
  },
  created() {
    // Plain fields: nothing in the template reads them.
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
    this.levels = [];
    this.blob = null;
    this.audioContext = null;
    this.analyser = null;
    this.sendWhenDone = false;
    this.discarded = false;
    this.isGone = false;
  },
  mounted() {
    this.start();
  },
  beforeUnmount() {
    this.isGone = true;
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.discarded = true;
      try {
        this.recorder.stop();
      } catch (error) {
        // Already stopped.
      }
    }
    this.release();
  },
};
</script>
